import { FC, useCallback, useState } from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { connect } from 'react-redux';
import i18next from 'i18next';
import { Segment } from '~src/interfaces/segment.interface';

import { DraggableIcon, DraggableInfo } from '~src/shared/lists/dragList.shared.sc';
import { validatorMaxLength80, validatorRequired } from '~src/services/validators';
import CaretBtn from '~src/shared/buttons/caretBtn/CaretBtn';
import TranslatableSectionForm from '~src/shared/form/translatableSectionForm/TranslatableSectionForm';
import { remoteSubmit } from '~src/shared/reactFinalForm/remoteSubmit';
import {
  deleteAttributeOption,
  editAttributeOptionSecure,
  editAttributeOptionForce,
  AttrOptionEditI,
} from '~src/modules/attributes/actions/attributes.actions';
import { translateContent } from '~src/services/translationServices';
import ActionConfirmDialog from '~src/shared/dialogs/actionConfirmDialog/ActionConfirmDialog';
import SvgIcon from '~src/shared/SvgIcon';
import FrameworkDelAction from '~src/shared/form/FrameworkDelAction';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import logger from '~src/services/logger/LlsLogger';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { AttributeOptionI } from '~src/interfaces/attributes.types';
import PeopleChips from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';
import { User } from '~src/interfaces/user.interface';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';

import {
  DelContainer,
  FormContainer,
  Caret,
  OptionItemContainer,
  ChipsWrapper,
} from './attributeOptionListItem.sc';
import OverrideConfirmationDialog from './OverrideConfirmationDialog';
import { VisibilityDescription, DescriptionContainer } from './attributeOptionListItem.sc';

interface OwnProps {
  t: i18next.TFunction;
  attributeId: string;
  item: AttributeOptionI;
}

interface StateProps {
  loadingEditOpt: boolean;
  loadingDelOpt: boolean;
}

interface DispatchProp {
  editAttributeOptionForce(payload: AttrOptionEditI, attributeId: string): ApiReduxResp;
  editAttributeOptionSecure(payload: AttrOptionEditI, attributeId: string): ApiReduxResp;
  deleteAttributeValue(attributeId: string, option: AttributeOptionI): ApiReduxResp;
}

type Props = OwnProps & StateProps & DispatchProp;

const AttributeOptionListItem: FC<Props> = props => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletionError, setDeletionError] = useState<undefined | string>(undefined);
  const [overridePeople, setOverridePeople] = useState<User[]>([]);
  const [popupLoading, setPopupLoading] = useState(false);

  const editFormSections = [
    {
      name: 'info',
      fields: [
        {
          name: 'attribute_options',
          label: 'x.shared.name_and_description_form.input.placeholder.name',
          value: props.item.value,
          validators: [validatorRequired, validatorMaxLength80],
          translationValidators: [validatorMaxLength80],
        },
      ],
    },
  ];

  const onEditSubmit = (payload: Record<string, any>) => {
    let peopleArr: string[] | undefined;
    if (payload.users) {
      peopleArr = payload.users.reduce((acc: string[], person: Segment) => {
        acc.push(person.id);
        return acc;
      }, []);
    }

    let segmentIds: string[] | undefined;
    if (payload.segments) {
      segmentIds = payload.segments.reduce((acc: string[], visibleTo: Segment) => {
        acc.push(visibleTo.id);
        return acc;
      }, []);
    }

    const sendData: any = {
      attribute_options: {
        [item.uuid]: payload.attribute_options,
      },
      users: peopleArr,
      segments: segmentIds,
    };
    if (Object.keys(payload.attribute_options).length < 1) {
      delete sendData.attribute_options;
    }
    if (overridePeople.length < 1) {
      return props.editAttributeOptionSecure(sendData, props.attributeId).then(res => {
        if (res.response && res.response.data.error) {
          setOverridePeople(Object.values(res.response.data.users));
        } else if (!res.error) {
          setShowEditForm(false);
        } else {
          if (res.error.response.data) {
            const oldKey = Object.keys(res.error.response.data)[0];
            const keys = oldKey.split('.');
            keys.splice(1, 1);
            const newKey = keys.join('.');
            res.error.response.data[newKey] = res.error.response.data[oldKey];
            delete res.error.response.data[oldKey];
          }
        }
        return res;
      });
    } else {
      setPopupLoading(true);
      return props
        .editAttributeOptionForce(sendData, props.attributeId)
        .then(res => {
          setOverridePeople([]);
          setShowEditForm(false);
          return res;
        })
        .finally(() => {
          setPopupLoading(false);
        });
    }
  };

  const toggleShowEditForm = () => {
    setShowEditForm(s => !s);
  };

  const toggleConfirmDeletionModal = () => {
    setConfirmDeletion(c => !c);
  };

  const handleDelete = () => {
    setDeleteLoading(true);
    props
      .deleteAttributeValue(props.attributeId, props.item)
      .then(({ error }) => {
        if (!error) {
          toggleConfirmDeletionModal();
        } else {
          throw Error(error.message);
        }
      })
      .catch(error => {
        logger.info('Error deleting AttributeOption', {
          error,
          info: {
            componentStack: 'AttributeOptionListItem.jsx',
          },
        });
        setDeletionError("Error! Can't delete attribute option.");
      })
      .finally(() => setDeleteLoading(false));
  };

  const renderVisibilityInput = useCallback(
    ({ input, meta: { error } }: FieldRenderProps<any>) => {
      const onChangeSegments = (param: any) => input.onChange(param);
      return (
        <ChipsWrapper>
          <PeopleChips
            t={props.t}
            fetchSuggestions={fetchSearchSegments}
            chipsName="segments"
            selectedPeople={props.item.segments}
            error={error}
            onChangePeople={onChangeSegments}
            placeHolderKey="x.attribute_option.visibility_segments.placeholder"
          />
        </ChipsWrapper>
      );
    },
    [props.item.segments],
  );

  const renderUsersInput = useCallback(({ input, meta: { error } }: FieldRenderProps<any>) => {
    const onChangePeople = (param: any) => input.onChange(param);
    return (
      <ChipsWrapper>
        <PeopleChips
          t={props.t}
          chipsName="users"
          selectedPeople={input.value ? input.value : []}
          error={error}
          onChangePeople={onChangePeople}
          filter="user"
          placeHolderKey="x.attribute_option.add_people.placeholder"
        />
      </ChipsWrapper>
    );
  }, []);

  const { t, item } = props;

  const submitDialog = () => {
    remoteSubmit(`editAttributeValue_${props.item.uuid}`);
  };

  const hideDialog = () => {
    setOverridePeople([]);
  };

  return (
    <>
      <OverrideConfirmationDialog
        headerTextKey="x.attribute_option.override_modal.header"
        titleKey="x.attribute_option.override_modal.title"
        subTitleKey="x.attribute_option.override_modal.sub_title"
        ctaTextKey="x.attribute_option.override_modal.cta_text"
        submitTextKey="x.attribute_option.override_modal.overwrite"
        cancelTextKey="x.attribute_option.override_modal.cancel"
        people={overridePeople}
        show={overridePeople.length > 0}
        onHide={hideDialog}
        onSubmit={submitDialog}
        loading={popupLoading}
        t={props.t}
      />
      <OptionItemContainer className={showEditForm ? 'is-open' : ''}>
        <DraggableInfo>
          <DraggableIcon>
            <SvgIcon name="iconDraggable" width="18px" height="14px" />
          </DraggableIcon>
          <span>{translateContent(item.value)}</span>
        </DraggableInfo>
        <Caret onClick={toggleShowEditForm}>
          <CaretBtn size="18px" open={showEditForm} />
        </Caret>
      </OptionItemContainer>
      <ActionConfirmDialog
        t={t}
        show={confirmDeletion}
        close
        onHide={toggleConfirmDeletionModal}
        actionTitleKey="x.attributes.option.delete_confirmation.headline"
        actionContentKey="x.attributes.option.delete_confirmation.content"
        actionCall={handleDelete}
        showDeletion
        loadingAction={deleteLoading}
        actionError={deletionError}
      />
      {showEditForm && (
        <FormContainer>
          <TranslatableSectionForm
            t={t}
            form={`editAttributeValue_${item.uuid}`}
            sections={editFormSections}
            loadingSave={props.loadingEditOpt}
            onSubmit={onEditSubmit}
            onCancel={toggleShowEditForm}
            inContainer
            hideButtons
          >
            <DescriptionContainer data-translation="x.attribute_option.users_add.description">
              {t('x.attribute_option.users_add.description')}
              <VisibilityDescription>
                <HoverInfoIcon
                  infoText={{
                    key: 'x.attribute_option.users_add.hint',
                    txt: t('x.attribute_option.users_add.hint'),
                  }}
                />
              </VisibilityDescription>
            </DescriptionContainer>
            <Field name="users" component={renderUsersInput} />
            <DescriptionContainer data-translation="x.attribute_option.visibility_segments.description">
              {t('x.attribute_option.visibility_segments.description')}
              <VisibilityDescription>
                <HoverInfoIcon
                  infoText={{
                    key: 'x.attribute_option.visibility_segments.hint',
                    txt: t('x.attribute_option.visibility_segments.hint'),
                  }}
                />
              </VisibilityDescription>
            </DescriptionContainer>
            <Field name="segments" component={renderVisibilityInput} />
          </TranslatableSectionForm>
          <DelContainer>
            <FrameworkDelAction
              t={t}
              delAction={toggleConfirmDeletionModal}
              isLoading={props.loadingDelOpt}
              isFramework={item.framework}
            />
          </DelContainer>
        </FormContainer>
      )}
    </>
  );
};

const mapStateToProps = ({ attributes }: StoreI): StateProps => ({
  loadingEditOpt: attributes.loadingEditOpt,
  loadingDelOpt: attributes.loadingDelOpt,
});

const mapDispatchToProps = (dispatch: any): DispatchProp => ({
  editAttributeOptionSecure: (payload, attributeId) =>
    dispatch(editAttributeOptionSecure(payload, attributeId)),
  editAttributeOptionForce: (payload, attributeId) =>
    dispatch(editAttributeOptionForce(payload, attributeId)),
  deleteAttributeValue: (attributeId, option) => dispatch(deleteAttributeOption(attributeId, option)),
});

const AttributeOptionListItemC = connect(mapStateToProps, mapDispatchToProps)(AttributeOptionListItem);

export default AttributeOptionListItemC;
